import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFloatMessage, MESSAGE_TYPE } from '../../../Providers/FloatMessage';
import { SecondaryBase } from '../../Commons/SecondaryBase';
import { Confirm } from '../../../Commons/Modals/Confirm';
import {
    GetSuggestions,
    GetActivePlaylist,
    GetMetrics,
    GetSettings,
    UpdateSettings,
    TogglePause,
    SetScreenMode,
    ProyectarScreen,
    ExportPlaylistToExcel
} from '../../../../utilities/BL/Admin/Overlays/Playlist';
import {
    ConnectWebSocketForPlaylistAdmin,
    DisconnectWebSocketForPlaylistAdmin
} from '../../../../utilities/WS/Admin/PlaylistWS';

import { PlaylistMetricsCards } from './PlaylistMetricsCards';
import { PlaylistCriticalControls } from './PlaylistCriticalControls';
import { PlaylistSuggestionsTable } from './PlaylistSuggestionsTable';
import { PlaylistActiveList } from './PlaylistActiveList';
import { PlaylistManualSongModal } from './PlaylistManualSongModal';
import { PlaylistEditSongModal } from './PlaylistEditSongModal';
import { PlaylistSettingsModal } from './PlaylistSettingsModal';
import { PlaylistThemeModal } from './PlaylistThemeModal';

/**
 * Consola Principal del Operador General del Estadio - Dinámica "Arma la Playlist" (Tile ID 46).
 * Incluye cabecera con control de overlay y modo de pantalla en vivo, métricas horizontales, panel dual con scrollbars independientes,
 * controles críticos colapsables y sincronización WebSocket en tiempo real.
 * 
 * @component PlaylistBoard
 * @param {Object} props
 * @param {Function} [props.getOutOfTheDynamic] - Callback para salir y volver al Dashboard administrativo.
 */
export const PlaylistBoard = ({ getOutOfTheDynamic }) => {
    const navigate = useNavigate();
    const { showMessage } = useFloatMessage();

    // Listas y estados principales
    const [suggestions, setSuggestions] = useState([]);
    const [activePlaylist, setActivePlaylist] = useState([]);
    const [metrics, setMetrics] = useState({
        totalSuggestions: 0,
        activeInPlaylist: 0,
        totalPlayed: 0,
        totalOperatorAdded: 0,
        uniqueFans: 0,
        topRequestedSongTitle: '',
        topRequestedSongVotes: 0
    });
    const [settings, setSettings] = useState({
        screenMode: 0,
        isSubmissionActive: true,
        submissionPausedMessage: '',
        isOverlayVisible: true,
        cooldownSeconds: 120,
        currentPlayingSongId: null
    });

    // Estados operativos
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [isTogglingOverlay, setIsTogglingOverlay] = useState(false);
    const [isChangingMode, setIsChangingMode] = useState(false);
    const [isProjectingScreen, setIsProjectingScreen] = useState(false);
    const [groupSimilar, setGroupSimilar] = useState(false);
    const [webSocketStatus, setWebSocketStatus] = useState('connecting');
    const [lastSyncTime, setLastSyncTime] = useState(null);

    // Modales
    const [showManualModal, setShowManualModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showThemeModal, setShowThemeModal] = useState(false);
    const [songToEdit, setSongToEdit] = useState(null);

    // Confirm modal
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
        message: '',
        action: null,
        textYes: 'Sí, confirmar',
        textNo: 'Cancelar'
    });

    // Carga general de datos
    const loadAllData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [sugRes, activeRes, metricsRes, settingsRes] = await Promise.all([
                GetSuggestions({ includeArchived: true, groupSimilar }),
                GetActivePlaylist(),
                GetMetrics(),
                GetSettings()
            ]);

            if (sugRes && sugRes.success && Array.isArray(sugRes.data)) {
                setSuggestions(sugRes.data);
            }
            if (activeRes && activeRes.success && Array.isArray(activeRes.data)) {
                setActivePlaylist(activeRes.data);
            }
            if (metricsRes && metricsRes.success && metricsRes.data) {
                setMetrics(metricsRes.data);
            }
            if (settingsRes && settingsRes.success && settingsRes.data) {
                setSettings(prev => ({ ...prev, ...settingsRes.data }));
            }
            setLastSyncTime(new Date().toLocaleTimeString('es-MX', { hour12: false }));
        } catch (err) {
            console.error('Error al sincronizar datos de Playlist:', err);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al sincronizar la información del servidor.',
                message: 'Error al sincronizar la información del servidor.'
            });
        } finally {
            setIsLoading(false);
        }
    }, [groupSimilar, showMessage]);

    // Manejador de eventos en tiempo real WebSocket
    const handleWebSocketPacket = useCallback((packet) => {
        if (!packet || typeof packet !== 'object') return;

        const action = packet.action;
        const data = packet.data;

        switch (action) {
            case 'suggestion_received':
                if (data) {
                    setSuggestions(prev => {
                        const exists = prev.some(s => s.id === data.id);
                        if (exists) return prev.map(s => s.id === data.id ? data : s);
                        return [data, ...prev];
                    });
                    setMetrics(prev => ({
                        ...prev,
                        totalSuggestions: (prev.totalSuggestions || 0) + 1
                    }));
                }
                break;
            case 'vote_added':
                if (data && data.suggestionId) {
                    setSuggestions(prev => prev.map(s => {
                        if (s.id === data.suggestionId) {
                            return { ...s, voteCount: data.newVoteCount };
                        }
                        return s;
                    }));
                }
                break;
            case 'suggestion_updated':
                if (data) {
                    setSuggestions(prev => prev.map(s => s.id === data.id ? data : s));
                    setActivePlaylist(prev => prev.map(s => s.id === data.id ? data : s));
                }
                break;
            case 'suggestion_archived':
                if (data && data.suggestionId) {
                    setSuggestions(prev => prev.map(s => s.id === data.suggestionId ? { ...s, isArchived: data.isArchived } : s));
                }
                break;
            case 'suggestion_deleted':
                if (data && data.suggestionId) {
                    setSuggestions(prev => prev.filter(s => s.id !== data.suggestionId));
                    setActivePlaylist(prev => prev.filter(s => s.id !== data.suggestionId));
                }
                break;
            case 'playlist_updated':
                if (Array.isArray(data)) {
                    setActivePlaylist(data);
                }
                break;
            case 'now_playing_changed':
                if (data) {
                    const currentId = data.currentSong ? data.currentSong.id : null;
                    setSettings(prev => ({ ...prev, currentPlayingSongId: currentId }));
                    setActivePlaylist(prev => prev.map(s => ({
                        ...s,
                        isNowPlaying: s.id === currentId
                    })));
                }
                break;
            case 'metrics_updated':
                if (data) {
                    setMetrics(data);
                }
                break;
            case 'settings_updated':
                if (data) {
                    setSettings(prev => ({ ...prev, ...data }));
                }
                break;
            case 'dynamic_reset':
                setSuggestions([]);
                setActivePlaylist([]);
                setMetrics({
                    totalSuggestions: 0,
                    activeInPlaylist: 0,
                    totalPlayed: 0,
                    totalOperatorAdded: 0,
                    uniqueFans: 0,
                    topRequestedSongTitle: '',
                    topRequestedSongVotes: 0
                });
                break;
            default:
                break;
        }
    }, []);

    // Conexión inicial de WebSocket
    useEffect(() => {
        loadAllData();

        ConnectWebSocketForPlaylistAdmin(
            handleWebSocketPacket,
            (status) => setWebSocketStatus(status),
            'superadmin'
        );

        return () => {
            DisconnectWebSocketForPlaylistAdmin();
        };
    }, [loadAllData, handleWebSocketPacket]);

    // Conmutador de visualización de Overlay (📺 isOverlayVisible)
    const handleToggleOverlay = async () => {
        setIsTogglingOverlay(true);
        const nextState = !settings.isOverlayVisible;
        try {
            const res = await UpdateSettings({
                ...settings,
                isOverlayVisible: nextState
            });

            if (res && res.success) {
                setSettings(prev => ({ ...prev, isOverlayVisible: nextState }));
                showMessage({
                    type: nextState ? MESSAGE_TYPE.SUCCESS : MESSAGE_TYPE.WARNING,
                    text: nextState
                        ? '📺 Overlay ACTIVADO en pantallas de estadio y transmisiones.'
                        : '📺 Overlay OCULTO de pantallas de estadio y transmisiones.',
                    message: nextState
                        ? '📺 Overlay ACTIVADO en pantallas de estadio y transmisiones.'
                        : '📺 Overlay OCULTO de pantallas de estadio y transmisiones.'
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: (res && res.message) || 'Error al actualizar visibilidad del overlay.',
                    message: (res && res.message) || 'Error al actualizar visibilidad del overlay.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al alternar el overlay.',
                message: 'Error de conexión al alternar el overlay.'
            });
        } finally {
            setIsTogglingOverlay(false);
        }
    };

    // Cambiar Modo de Pantalla Gigante (0: QR, 1: Now Playing, 2: Agregadas/En Cola)
    const handleScreenModeChange = async (mode) => {
        const nextMode = Number(mode);
        if (Number(settings.screenMode) === nextMode) return;

        setIsChangingMode(true);
        try {
            const res = await UpdateSettings({
                ...settings,
                screenMode: nextMode
            });

            if (res && res.success) {
                setSettings(prev => ({ ...prev, screenMode: nextMode }));
                const modeLabels = {
                    0: 'Mostrar QR en pantalla (Convocatoria)',
                    1: 'Mostrar canción en reproducción (Now Playing)',
                    2: 'Mostrar canciones agregadas (En Cola)'
                };
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `📺 Modo de Pantalla Gigante: ${modeLabels[nextMode] || nextMode}`,
                    message: `📺 Modo de Pantalla Gigante: ${modeLabels[nextMode] || nextMode}`
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: (res && res.message) || 'Error al actualizar modo de pantalla.',
                    message: (res && res.message) || 'Error al actualizar modo de pantalla.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al cambiar el modo de pantalla.',
                message: 'Error de conexión al cambiar el modo de pantalla.'
            });
        } finally {
            setIsChangingMode(false);
        }
    };

    // Proyectar pantalla en el estadio (Screen / Pantalla Gigante)
    const handleProyectarScreen = async () => {
        setIsProjectingScreen(true);
        try {
            const res = await ProyectarScreen(settings.screenMode);
            if (res && res.success) {
                const modeLabels = {
                    0: 'Mostrar QR en pantalla (Convocatoria)',
                    1: 'Mostrar canción en reproducción (Now Playing)',
                    2: 'Mostrar canciones agregadas (En Cola)'
                };
                const activeLabel = modeLabels[Number(settings.screenMode)] || 'Modo seleccionado';
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `📺 Señal enviada: Proyectando en el estadio "${activeLabel}".`,
                    message: `📺 Señal enviada: Proyectando en el estadio "${activeLabel}".`
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: (res && res.message) || 'Error al proyectar en la pantalla del estadio.',
                    message: (res && res.message) || 'Error al proyectar en la pantalla del estadio.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de comunicación al proyectar pantalla.',
                message: 'Error de comunicación al proyectar pantalla.'
            });
        } finally {
            setIsProjectingScreen(false);
        }
    };

    // Pausar / Reanudar sugerencias de aficionados desde CriticalControls
    const handleTogglePause = async (nextState) => {
        setSettings(prev => ({ ...prev, isSubmissionActive: nextState }));
    };

    // Exportar datos a Excel / CSV
    const handleExport = async () => {
        setIsExporting(true);
        try {
            const res = await ExportPlaylistToExcel();
            if (res && res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '📥 Descarga del reporte de Playlist generada con éxito.',
                    message: '📥 Descarga del reporte de Playlist generada con éxito.'
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: (res && res.message) || 'Error al generar el archivo.',
                    message: (res && res.message) || 'Error al generar el archivo.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al exportar archivo.',
                message: 'Error de conexión al exportar archivo.'
            });
        } finally {
            setIsExporting(false);
        }
    };

    // Retorno al Dashboard
    const handleExit = () => {
        if (getOutOfTheDynamic && typeof getOutOfTheDynamic === 'function') {
            getOutOfTheDynamic();
        } else {
            navigate('/admin/dashboard');
        }
    };

    // Abrir modal de edición
    const handleOpenEdit = (song) => {
        setSongToEdit(song);
        setShowEditModal(true);
    };

    return (
        <SecondaryBase actionToExecute={handleExit}>
            {/* Modal de Confirmación Global */}
            {showConfirm && (
                <Confirm
                    message={confirmConfig.message}
                    actionToYesButton={confirmConfig.action}
                    textYesButton={confirmConfig.textYes}
                    textNoButton={confirmConfig.textNo}
                    setShowModal={setShowConfirm}
                />
            )}

            {/* Modal para Agregar Canción Manualmente */}
            <PlaylistManualSongModal
                isOpen={showManualModal}
                onClose={() => setShowManualModal(false)}
                onSuccess={loadAllData}
            />

            {/* Modal para Editar Canción */}
            <PlaylistEditSongModal
                isOpen={showEditModal}
                song={songToEdit}
                onClose={() => {
                    setShowEditModal(false);
                    setSongToEdit(null);
                }}
                onSuccess={loadAllData}
            />

            {/* Modal de Configuración Operativa */}
            <PlaylistSettingsModal
                isOpen={showSettingsModal}
                onClose={() => setShowSettingsModal(false)}
                onSuccess={loadAllData}
            />

            {/* Modal de Personalización de Tema Visual */}
            <PlaylistThemeModal
                isOpen={showThemeModal}
                onClose={() => setShowThemeModal(false)}
                onSuccess={loadAllData}
            />

            <div className="container-fluid px-4 py-3 PlaylistBoard">
                {/* 1. Header con Título, Subtítulo y Estado WebSocket */}
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4 border-bottom pb-3">
                    <div>
                        <h1 className="h3 fw-bold text-dark mb-1">
                            <i className="bi bi-music-note-beamed text-danger me-2"></i>
                            Arma la Playlist
                            <span className="badge bg-danger-subtle text-danger ms-2 fs-6 fw-bold">
                                Tile 46
                            </span>
                        </h1>
                        <p className="text-secondary small mb-0">
                            Consola de Operación General del Estadio • Moderación y Curaduría en Vivo
                        </p>
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <div
                            className={`badge d-flex align-items-center gap-2 px-3 py-2 fs-6 ${
                                webSocketStatus === 'connected'
                                    ? 'bg-success-subtle text-success border border-success'
                                    : 'bg-warning-subtle text-warning border border-warning'
                            }`}
                            title={webSocketStatus === 'connected' ? 'Sincronizado vía WebSocket' : 'Intentando reconectar...'}
                        >
                            <span
                                className={`spinner-grow spinner-grow-sm ${
                                    webSocketStatus === 'connected' ? 'text-success' : 'text-warning'
                                }`}
                                style={{ width: '8px', height: '8px' }}
                            ></span>
                            {webSocketStatus === 'connected' ? 'En Vivo' : 'Reconectando...'}
                        </div>
                    </div>
                </div>

                {/* 2. Sección Superior: Pantalla del estadio y Overlay (Estilo Dinosaur.js / QuizzB9ZoneBoard.js) */}
                <div className="row g-4 mb-4">
                    <div className="col-12">
                        <div className="bg-light p-4 rounded-3 border shadow-sm">
                            <p className="fs-4 fw-bold text-center mb-3">Pantalla del estadio y Overlay</p>

                            {/* Estado del banner/overlay con botón toggle independiente */}
                            <div className="mb-4 text-center">
                                <p className="fs-5 mb-2">
                                    Estatus:{' '}
                                    <span
                                        className={`fw-bold ${
                                            settings.isOverlayVisible ? 'text-success' : 'text-danger'
                                        }`}
                                    >
                                        {settings.isOverlayVisible
                                            ? 'El banner se está mostrando en pantalla'
                                            : 'El banner está oculto'}
                                    </span>
                                </p>
                                <button
                                    type="button"
                                    onClick={handleToggleOverlay}
                                    disabled={isTogglingOverlay}
                                    className={`btn w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 transition-all shadow-sm ${
                                        settings.isOverlayVisible
                                            ? 'btn-outline-danger'
                                            : 'btn-outline-success'
                                    }`}
                                >
                                    <i
                                        className={`bi ${
                                            settings.isOverlayVisible ? 'bi-eye-slash-fill' : 'bi-eye-fill'
                                        }`}
                                    ></i>
                                    {isTogglingOverlay
                                        ? 'Cambiando...'
                                        : settings.isOverlayVisible
                                        ? 'Ocultar overlay'
                                        : 'Mostrar overlay'}
                                </button>
                            </div>

                            {/* 3 Botones independientes para modo de pantalla gigante */}
                            <div className="mb-2">
                                <label className="form-label fw-semibold text-dark text-center small d-block mb-2">
                                    Elige lo que deseas proyectar en la pantalla gigante
                                </label>
                                <div className="d-flex flex-wrap gap-2 justify-content-center">
                                    <button
                                        type="button"
                                        onClick={() => handleScreenModeChange(0)}
                                        disabled={isChangingMode}
                                        className={`btn py-2.5 px-3 fw-bold flex-grow-1 d-flex align-items-center justify-content-center gap-2 ${
                                            Number(settings.screenMode) === 0
                                                ? 'btn-primary shadow-sm'
                                                : 'btn-outline-secondary'
                                        }`}
                                    >
                                        {Number(settings.screenMode) === 0 && (
                                            <i className="bi bi-check-circle-fill"></i>
                                        )}
                                        <i className="bi bi-qr-code"></i>
                                        Mostrar QR en pantalla
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleScreenModeChange(1)}
                                        disabled={isChangingMode}
                                        className={`btn py-2.5 px-3 fw-bold flex-grow-1 d-flex align-items-center justify-content-center gap-2 ${
                                            Number(settings.screenMode) === 1
                                                ? 'btn-primary shadow-sm'
                                                : 'btn-outline-secondary'
                                        }`}
                                    >
                                        {Number(settings.screenMode) === 1 && (
                                            <i className="bi bi-check-circle-fill"></i>
                                        )}
                                        <i className="bi bi-music-note-beamed"></i>
                                        Mostrar canción en reproducción
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleScreenModeChange(2)}
                                        disabled={isChangingMode}
                                        className={`btn py-2.5 px-3 fw-bold flex-grow-1 d-flex align-items-center justify-content-center gap-2 ${
                                            Number(settings.screenMode) === 2
                                                ? 'btn-primary shadow-sm'
                                                : 'btn-outline-secondary'
                                        }`}
                                    >
                                        {Number(settings.screenMode) === 2 && (
                                            <i className="bi bi-check-circle-fill"></i>
                                        )}
                                        <i className="bi bi-card-list"></i>
                                        Mostrar canciones agregadas
                                    </button>
                                </div>

                                {/* Botón para Proyectar pantalla en el estadio */}
                                <div className="mt-3">
                                    <button
                                        type="button"
                                        onClick={handleProyectarScreen}
                                        disabled={isProjectingScreen || isChangingMode}
                                        className="btn btn-success w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm transition-all text-white border-0"
                                        style={{ fontSize: '1.05rem' }}
                                    >
                                        <i className="bi bi-projector-fill fs-5"></i>
                                        {isProjectingScreen
                                            ? 'Proyectando en el estadio...'
                                            : 'Proyectar pantalla en el estadio'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 3. Sección de Estadísticas y Barra de Acciones */}
                <div className="text-center mt-4 mb-3">
                    <h2 className="fs-4 fw-bold text-dark mb-1">Estadísticas en Tiempo Real</h2>
                    <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap mt-2">
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm fw-semibold"
                            onClick={loadAllData}
                            disabled={isLoading}
                        >
                            <i className={`bi bi-arrow-clockwise me-1 ${isLoading ? 'spin' : ''}`}></i>
                            Recargar estadísticas y listado
                        </button>
                        {lastSyncTime && (
                            <div className="text-muted small">
                                <i className="bi bi-clock-history me-1"></i> Última sincronización: {lastSyncTime} hrs
                            </div>
                        )}
                    </div>
                </div>

                {/* Barra de Botones de Acción */}
                <div className="d-flex justify-content-end align-items-center gap-2 flex-wrap mb-3">
                    <button
                        type="button"
                        className="btn btn-danger fw-bold d-flex align-items-center gap-2 shadow-sm"
                        onClick={() => setShowManualModal(true)}
                    >
                        <i className="bi bi-plus-circle-fill"></i>
                        + Agregar Canción
                    </button>

                    <button
                        type="button"
                        className="btn btn-outline-dark fw-bold d-flex align-items-center gap-2"
                        onClick={() => setShowThemeModal(true)}
                    >
                        <i className="bi bi-palette-fill text-danger"></i>
                        Tema 🎨
                    </button>

                    <button
                        type="button"
                        className="btn btn-outline-secondary fw-bold d-flex align-items-center gap-2"
                        onClick={() => setShowSettingsModal(true)}
                    >
                        <i className="bi bi-gear-fill text-primary"></i>
                        Ajustes ⚙️
                    </button>

                    <button
                        type="button"
                        className="btn btn-outline-success fw-bold d-flex align-items-center gap-2"
                        onClick={handleExport}
                        disabled={isExporting}
                    >
                        <i className="bi bi-file-earmark-excel-fill"></i>
                        {isExporting ? 'Exportando...' : 'Exportar Excel 📥'}
                    </button>
                </div>

                {/* Carril de Tarjetas de Métricas */}
                <div className="PlaylistBoard__MetricsContainer mb-4">
                    <PlaylistMetricsCards metrics={metrics} />
                </div>

                {/* 4. Espacio de Trabajo Dual (Buzón de Sugerencias a la izquierda, Playlist Activa a la derecha) */}
                <div className="PlaylistBoard__CentralContainer mb-4">
                    <div className="PlaylistBoard__DualWorkspace">
                        {/* Panel Izquierdo: Buzón de Sugerencias */}
                        <div className="PlaylistBoard__LeftColumn">
                            <PlaylistSuggestionsTable
                                suggestions={suggestions}
                                isLoading={isLoading}
                                groupSimilar={groupSimilar}
                                onToggleGroupSimilar={(val) => setGroupSimilar(val)}
                                onEdit={handleOpenEdit}
                                onRefresh={loadAllData}
                            />
                        </div>

                        {/* Panel Derecho: Playlist Activa del Estadio */}
                        <div className="PlaylistBoard__RightColumn">
                            <PlaylistActiveList
                                activePlaylist={activePlaylist}
                                currentPlayingSongId={settings.currentPlayingSongId}
                                onRefresh={loadAllData}
                                onPlaylistChange={setActivePlaylist}
                            />
                        </div>
                    </div>
                </div>

                {/* 5. Zona de Peligro / Controles Críticos Colapsable en la Parte Inferior */}
                <div className="PlaylistBoard__CriticalContainer mb-4">
                    <PlaylistCriticalControls
                        isSubmissionActive={settings.isSubmissionActive}
                        onTogglePause={handleTogglePause}
                        onResetSuccess={loadAllData}
                    />
                </div>
            </div>
        </SecondaryBase>
    );
};

export default PlaylistBoard;
